import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Users, Briefcase, Calendar, Mail, Phone, Building, TrendingUp, BarChart3, PieChart, Activity, LogOut } from 'lucide-react';

const Admin = () => {
  const [sectorContacts, setSectorContacts] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [generalContacts, setGeneralContacts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Check authentication on component mount
  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch analytics
      const analyticsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics`);
      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        setAnalytics(analyticsData);
      }

      // Fetch sector contacts
      const sectorResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sector-contacts`);
      if (sectorResponse.ok) {
        const sectorData = await sectorResponse.json();
        setSectorContacts(sectorData);
      }

      // Fetch service requests
      const serviceResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/service-requests`);
      if (serviceResponse.ok) {
        const serviceData = await serviceResponse.json();
        setServiceRequests(serviceData);
      }

      // Fetch general contacts
      const generalResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`);
      if (generalResponse.ok) {
        const generalData = await generalResponse.json();
        setGeneralContacts(generalData);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load submissions. Please try again.');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const ContactCard = ({ contact, type }) => (
    <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-emerald-700 mb-4">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">
            {contact.firstName || contact.first_name} {contact.lastName || contact.last_name}
          </h3>
          <p className="text-sm text-gray-500">{formatDate(contact.timestamp)}</p>
        </div>
        <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-full">
          {contact.form_type || type}
        </span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center text-gray-700">
          <Building className="w-4 h-4 mr-2" />
          <span className="text-sm">{contact.company}</span>
        </div>
        <div className="flex items-center text-gray-700">
          <Mail className="w-4 h-4 mr-2" />
          <span className="text-sm">{contact.email}</span>
        </div>
        <div className="flex items-center text-gray-700">
          <Phone className="w-4 h-4 mr-2" />
          <span className="text-sm">{contact.phone}</span>
        </div>
        {contact.facilityLocation && (
          <div className="flex items-center text-gray-700">
            <Calendar className="w-4 h-4 mr-2" />
            <span className="text-sm">{contact.facilityLocation}</span>
          </div>
        )}
      </div>

      {contact.additionalInfo && (
        <div className="mt-4 p-3 bg-gray-50 rounded">
          <p className="text-sm text-gray-700">
            <strong>Additional Info:</strong> {contact.additionalInfo}
          </p>
        </div>
      )}

      {contact.message && (
        <div className="mt-4 p-3 bg-gray-50 rounded">
          <p className="text-sm text-gray-700">
            <strong>Message:</strong> {contact.message}
          </p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">View and manage all form submissions</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Sector Inquiries</p>
                <p className="text-3xl font-bold text-gray-900">{sectorContacts.length}</p>
              </div>
              <Users className="w-12 h-12 text-blue-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Service Requests</p>
                <p className="text-3xl font-bold text-gray-900">{serviceRequests.length}</p>
              </div>
              <Briefcase className="w-12 h-12 text-emerald-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">General Contacts</p>
                <p className="text-3xl font-bold text-gray-900">{generalContacts.length}</p>
              </div>
              <FileText className="w-12 h-12 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('analytics')}
                className={`py-4 px-6 font-medium text-sm ${
                  activeTab === 'analytics'
                    ? 'border-b-2 border-emerald-700 text-emerald-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Analytics
                </div>
              </button>
              <button
                onClick={() => setActiveTab('sector')}
                className={`py-4 px-6 font-medium text-sm ${
                  activeTab === 'sector'
                    ? 'border-b-2 border-emerald-700 text-emerald-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sector Contacts ({sectorContacts.length})
              </button>
              <button
                onClick={() => setActiveTab('service')}
                className={`py-4 px-6 font-medium text-sm ${
                  activeTab === 'service'
                    ? 'border-b-2 border-emerald-700 text-emerald-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Service Requests ({serviceRequests.length})
              </button>
              <button
                onClick={() => setActiveTab('general')}
                className={`py-4 px-6 font-medium text-sm ${
                  activeTab === 'general'
                    ? 'border-b-2 border-emerald-700 text-emerald-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                General Contacts ({generalContacts.length})
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'analytics' && analytics && (
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg border-2 border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <FileText className="w-8 h-8 text-blue-600" />
                      <TrendingUp className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{analytics.summary.total_submissions}</p>
                    <p className="text-sm text-blue-700">Total Submissions</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-lg border-2 border-emerald-200">
                    <div className="flex items-center justify-between mb-2">
                      <Users className="w-8 h-8 text-emerald-600" />
                      <Activity className="w-5 h-5 text-emerald-500" />
                    </div>
                    <p className="text-2xl font-bold text-emerald-900">{analytics.summary.recent_submissions_7d}</p>
                    <p className="text-sm text-emerald-700">Last 7 Days</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg border-2 border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <Briefcase className="w-8 h-8 text-purple-600" />
                      <BarChart3 className="w-5 h-5 text-purple-500" />
                    </div>
                    <p className="text-2xl font-bold text-purple-900">{analytics.summary.sector_contacts}</p>
                    <p className="text-sm text-purple-700">Sector Inquiries</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-lg border-2 border-amber-200">
                    <div className="flex items-center justify-between mb-2">
                      <Building className="w-8 h-8 text-amber-600" />
                      <PieChart className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="text-2xl font-bold text-amber-900">{analytics.summary.service_requests}</p>
                    <p className="text-sm text-amber-700">Service Requests</p>
                  </div>
                </div>

                {/* Timeline Chart */}
                <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-emerald-600" />
                    Submissions Timeline (Last 30 Days)
                  </h3>
                  {analytics.timeline && analytics.timeline.length > 0 ? (
                    <div className="space-y-2">
                      <div className="flex items-end space-x-2 h-48">
                        {analytics.timeline.map((day, index) => {
                          const maxCount = Math.max(...analytics.timeline.map(d => d.count));
                          const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                          return (
                            <div key={index} className="flex-1 flex flex-col items-center justify-end">
                              <div 
                                className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t-md hover:from-emerald-700 hover:to-emerald-500 transition-colors cursor-pointer relative group"
                                style={{ height: `${height}%`, minHeight: day.count > 0 ? '8px' : '0' }}
                                title={`${day.date}: ${day.count} submissions`}
                              >
                                <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                  {day.count}
                                </span>
                              </div>
                              {index % 5 === 0 && (
                                <span className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left">
                                  {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No submission data available</p>
                  )}
                </div>

                {/* Form Type Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <PieChart className="w-5 h-5 mr-2 text-blue-600" />
                      Submissions by Type
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(analytics.form_type_breakdown).map(([type, count]) => {
                        const percentage = (count / analytics.summary.total_submissions * 100).toFixed(1);
                        return (
                          <div key={type} className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700 capitalize">
                                {type.replace(/_/g, ' ')}
                              </span>
                              <span className="text-sm font-bold text-gray-900">{count} ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-emerald-500 h-2 rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sector Breakdown */}
                  <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <BarChart3 className="w-5 h-5 mr-2 text-emerald-600" />
                      Top Sectors
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(analytics.sector_breakdown)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 5)
                        .map(([sector, count]) => {
                          const percentage = analytics.summary.sector_contacts > 0 
                            ? (count / analytics.summary.sector_contacts * 100).toFixed(1)
                            : 0;
                          return (
                            <div key={sector} className="space-y-1">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700 capitalize">
                                  {sector.replace(/_/g, ' ')}
                                </span>
                                <span className="text-sm font-bold text-gray-900">{count} ({percentage}%)</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-gradient-to-r from-emerald-500 to-green-500 h-2 rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'sector' && (
              <div>
                {sectorContacts.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No sector inquiries yet.</p>
                ) : (
                  sectorContacts.map((contact) => (
                    <ContactCard key={contact.id} contact={contact} type="sector" />
                  ))
                )}
              </div>
            )}

            {activeTab === 'service' && (
              <div>
                {serviceRequests.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No service requests yet.</p>
                ) : (
                  serviceRequests.map((request) => (
                    <ContactCard key={request.id} contact={request} type="service" />
                  ))
                )}
              </div>
            )}

            {activeTab === 'general' && (
              <div>
                {generalContacts.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No general contacts yet.</p>
                ) : (
                  generalContacts.map((contact) => (
                    <ContactCard key={contact.id} contact={contact} type="general" />
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Refresh Button */}
        <div className="text-center">
          <button
            onClick={fetchData}
            className="px-6 py-3 bg-emerald-700 text-white rounded-lg font-semibold hover:bg-emerald-800 transition-colors"
          >
            Refresh Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default Admin;
